import { z } from "zod";

export const VirtualPlatformSchema = z.enum({
  ZOOM: "zoom",
  GOOGLE_MEET: "google_meet",
  TEAMS: "teams",
  WEBEX: "webex",
  OTHER: "other",
});

export const VIRTUAL_PLATFORM_VALUES = VirtualPlatformSchema.enum;

export const EVENT_LOCATION_TYPE_VALUES = {
  PHYSICAL: "physical",
  VIRTUAL: "virtual",
  NONE: "none",
} as const;

export type EventLocationTypeValueType =
  (typeof EVENT_LOCATION_TYPE_VALUES)[keyof typeof EVENT_LOCATION_TYPE_VALUES];

export const EventLocationCoordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

export const PhysicalEventLocationSchema = z.object({
  type: z.literal("physical"),
  placeId: z.string().min(1),
  address: z.string().min(1),
  name: z.string().optional(),
  coordinates: EventLocationCoordinatesSchema,
  timezone: z.string().min(1), // IANA timezone
  description: z.string().max(500).optional(),
});

export const VirtualEventLocationSchema = z.object({
  type: z.literal("virtual"),
  url: z.url(),
  platform: VirtualPlatformSchema.optional(),
});

export const NoEventLocationSchema = z.object({
  type: z.literal("none"),
});

export const EventLocationSchema = z.discriminatedUnion("type", [
  PhysicalEventLocationSchema,
  VirtualEventLocationSchema,
  NoEventLocationSchema,
]);
export type VirtualPlatformType = z.infer<typeof VirtualPlatformSchema>;

export type PhysicalEventLocation = z.infer<typeof PhysicalEventLocationSchema>;

export type VirtualEventLocation = z.infer<typeof VirtualEventLocationSchema>;

export type NoEventLocation = z.infer<typeof NoEventLocationSchema>;

export type EventLocationType = z.infer<typeof EventLocationSchema>;

export const EventLocation = {
  isPhysical(location: EventLocationType): location is PhysicalEventLocation {
    return location.type === EVENT_LOCATION_TYPE_VALUES.PHYSICAL;
  },

  isVirtual(location: EventLocationType): location is VirtualEventLocation {
    return location.type === EVENT_LOCATION_TYPE_VALUES.VIRTUAL;
  },

  isNone(location: EventLocationType): location is NoEventLocation {
    return location.type === EVENT_LOCATION_TYPE_VALUES.NONE;
  },

  none(): NoEventLocation {
    return { type: EVENT_LOCATION_TYPE_VALUES.NONE };
  },

  isValidType(value: string): value is EventLocationTypeValueType {
    return Object.values(EVENT_LOCATION_TYPE_VALUES).includes(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Safe after Object.values check
      value as EventLocationTypeValueType,
    );
  },
} as const;
